import { apiBase } from './apiBase.js';

/**
 * Anonymous aggregate analytics.
 *
 * Hard rules (mirrored by the API's allow-list):
 *  - never send pixels, landmarks, file names or any user content;
 *  - only the fixed event vocabulary below;
 *  - failures are swallowed: analytics must never break the validator;
 *  - this is the ONLY network call left in the frontend (the photo is never
 *    uploaded), and it stays disabled while `VITE_VALIDATOR_API_BASE` is empty.
 */

export const EVENT_NAMES = [
  'validator_open',
  'document_selected',
  'photo_uploaded',
  'camera_opened',
  'validation_started',
  'validation_completed',
  'validation_pass',
  'validation_fail',
  'validation_uncertain',
  'cta_clicked',
  'failed_rule',
  'server_fallback_requested',
] as const;

export type EventName = (typeof EVENT_NAMES)[number];

export type EventProperties = Record<string, string | number | boolean | undefined>;

export interface Analytics {
  track(name: EventName, properties?: EventProperties): void;
}

const scrub = (properties: EventProperties = {}): Record<string, string | number | boolean> => {
  const clean: Record<string, string | number | boolean> = {};
  for (const [key, value] of Object.entries(properties)) {
    if (value === undefined || value === null) continue;
    if (typeof value === 'number' && !Number.isFinite(value)) continue;
    // Guard rail: never let a long free-form string slip through.
    if (typeof value === 'string' && value.length > 120) continue;
    clean[key] = value;
  }
  return clean;
};

export const createAnalytics = (options: { enabled?: boolean } = {}): Analytics => {
  const base = apiBase();
  let enabled = options.enabled !== false && base.length > 0;
  const queue: Array<{ name: EventName; properties: EventProperties }> = [];

  const flush = (): void => {
    if (!enabled || queue.length === 0) return;
    const events = queue.splice(0, queue.length).map((event) => ({
      name: event.name,
      properties: scrub(event.properties),
    }));
    const body = JSON.stringify({ events });
    try {
      if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
        const blob = new Blob([body], { type: 'application/json' });
        if (navigator.sendBeacon(`${base}/api/v1/events`, blob)) return;
      }
      void fetch(`${base}/api/v1/events`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body,
        keepalive: true,
      }).catch(() => undefined);
    } catch {
      // Analytics is best-effort by design.
    }
  };

  return {
    track: (name, properties = {}) => {
      if (!EVENT_NAMES.includes(name)) return;
      if (!enabled) return;
      queue.push({ name, properties });
      // Small batches keep the number of requests low without delaying data.
      if (queue.length >= 8) flush();
      else if (typeof setTimeout === 'function') setTimeout(flush, 1500);
    },
  };
};

/** Device class only — no fingerprinting, no user agent string. */
export const deviceClass = (): 'mobile' | 'tablet' | 'desktop' => {
  if (typeof navigator === 'undefined') return 'desktop';
  const width = typeof window !== 'undefined' ? window.innerWidth : 1280;
  const touch = 'ontouchstart' in window || (navigator.maxTouchPoints ?? 0) > 0;
  if (!touch) return 'desktop';
  return width < 720 ? 'mobile' : 'tablet';
};
